import { Component, Event, EventEmitter, h, Listen, Prop, State, Watch, Element } from '@stencil/core';
import {
  logDebug,
  logError,
  BridgeUpdateEvent,
  BridgeStateEvent,
  QuestionType,
  BridgeState,
  BridgeUpdate,
  BridgeCloseEvent,
  TransitionType,
  QuestionAnsweredEvent,
  BridgeAnswer,
  BridgeError,
} from '@hpfc/fc-frontend-core';

import { QUERY_INFO_CLASS } from '../results-found/ResultsFoundConstants';

/**
 * Modal component for showing the bridge.
 */
@Component({
  tag: 'fccd-bridge-modal',
  styleUrl: 'bridge-modal.scss',
})
export class BridgeModal {
  /**
   * element reference to bridge modal
   */
  @Element() element: HTMLElement;
  /**
   * Show/hide the modal
   */
  @Prop()
  visible: boolean = false;

  /**
   * Header of modal
   */
  @Prop()
  header: string = '';
  /**
   * Event that is sent to the state manager when the modal is dismissed.
   */
  @Event()
  fccdBridgeCloseEvent: EventEmitter<BridgeCloseEvent>;

  /**
   * Event to indicate that a selection has been made in the modal, and new data is needed.
   */
  @Event() fccdBridgeUpdateEvent: EventEmitter<BridgeUpdateEvent>;

  /**
   * Current question selection
   */
  @State()
  selectedAnswers: Array<BridgeAnswer> = null;
  /**
   * State of the child checkValidation
   */
  @State()
  checkValidation: boolean = false;
  /**
   * State of the modal visibility
   */
  @State()
  isVisible: boolean = false;

  /**
   * State of the modal visibility
   */
  @State()
  isAwaitingCallback: boolean = false;

  /**
   * State of the modal visibility
   */
  @State()
  bridgeState: BridgeState;

  /**
   * State of the screenreader focus
   */
  @State()
  settingButtonFocus: boolean = false;

  /**
   * Watch for changes in the visibility triggered by the parent component.
   */
  @Watch('visible')
  handleVisibleChange(newValue: boolean) {
    this.setVisibility(newValue);
  }

  /**
   * Set the visibility to match when the component loads, watch will not
   * catch.
   */
  componentWillLoad() {
    this.checkValidation = false;
    this.setVisibility(this.visible);
  }

  private setVisibility(visible: boolean) {
    if (visible) {
      setTimeout(
        (() => {
          this.isVisible = visible;
        }).bind(this),
        700,
      );
    } else {
      this.isVisible = visible;
    }
  }

  @Listen('fccdBridgeStateEvent', { target: 'document' })
  bridgeStateHandler(event: CustomEvent<BridgeStateEvent>) {
    //Need a bridge state to do anything.
    if (!event?.detail) {
      logError(`bridgeStateHandler event thrown with no detail`);
      return;
    }

    if (event.detail.bridgeState) {
      logDebug(`bridgeStateHandler`, event.detail.bridgeState);
      this.bridgeState = event.detail.bridgeState;
      this.setVisibility(true);
      this.isAwaitingCallback = false;
      // setting focus during component rendering
      this.settingButtonFocus = true;
    }

    if (event.detail.isError === true) {
      logError(`bridgeStateHandler bad error happened`, event.detail.error);
    }
  }

  componentDidRender() {
    // we want to set the focus to the "X" close button when continue/next is pressed
    // preferred would be to go to the H1, but headings are not focusable elements
    // and adding tabIndex messes with the tab order of the entire page.
    // also, adding a check for no selected answers, otherwise when a user selects an answer it throws a new
    // event which causes the focus to shift to the close button again
    if (this.settingButtonFocus && !this.selectedAnswers) {
      const closeButton: HTMLElement = this.element.querySelector('hp5-button-close > button');
      const focusEl = closeButton ? closeButton : null;
      if (focusEl) {
        focusEl.focus();
      }
    }
  }

  @Listen('fccdBridgeCloseEvent', { target: 'document' })
  bridgeCloseHandler(event: CustomEvent<BridgeCloseEvent>) {
    logDebug(`bridgeCloseHandler`, event);
    this.bridgeClose();
  }

  @Listen('questionAnsweredEvent')
  questionAnswerHandler(event: CustomEvent<QuestionAnsweredEvent>) {
    if (event.detail.answers !== null && event.detail.answers.length > 0) {
      this.checkValidation = false;
      this.selectedAnswers = event.detail.answers;
      if (event.detail.validating === true) {
        const isNext = new CustomEvent('isNext', {
          detail: true,
        });
        this.confirmHandler(isNext);
      }
    } else if (event.detail.cleared === true) {
      this.selectedAnswers = null;
    }
  }

  @Listen('confirmed')
  confirmHandler(event: CustomEvent<boolean>) {
    const isPrimaryButton = event.detail;
    const { bridgeState: state, selectedAnswers } = this;
    const isErrorQuestion = state?.bridgeData?.questionType === QuestionType.error;

    if (isPrimaryButton === false) {
      const bridgeUpdate = new BridgeUpdate(state.scenario, state.bridgeData.identifier, null, TransitionType.back);
      this.fccdBridgeUpdateEvent.emit(new BridgeUpdateEvent(bridgeUpdate));
      this.resetValidationState();
      return;
    }

    const allQuestionsAnswered = selectedAnswers?.every(answer => answer?.isAnswered());
    if (isPrimaryButton && (allQuestionsAnswered || isErrorQuestion || state?.bridgeData?.questionType === QuestionType.summary)) {
      const bridgeUpdate = new BridgeUpdate(state.scenario, state.bridgeData.identifier, !isErrorQuestion ? selectedAnswers : null, TransitionType.next);
      this.fccdBridgeUpdateEvent.emit(new BridgeUpdateEvent(bridgeUpdate));
      this.resetValidationState();
      return;
    }

    this.checkValidation = true;
  }

  @Listen('dismiss')
  dismissHandler(event: CustomEvent<any>) {
    logDebug(`dismissHandler`, event);

    const { bridgeState: state } = this;

    //If state is gone, we can't do anything, with the update.
    if (state) {
      const bridgeUpdate = new BridgeUpdate(state.scenario, state.bridgeData.identifier, null, TransitionType.dismiss);
      this.fccdBridgeUpdateEvent.emit(new BridgeUpdateEvent(bridgeUpdate));
    }

    this.fccdBridgeCloseEvent.emit(new BridgeCloseEvent());
  }

  /**
   * will listen for child update events, and sets isAwaitingCallback to true.
   * Note: may use this later to create spinner if needed.
   */
  @Listen('fccdBridgeUpdateEvent')
  bridgeUpdateHandler(event: CustomEvent<BridgeUpdateEvent>) {
    if (event?.detail?.bridgeUpdate) {
      this.isAwaitingCallback = true;
    }
  }

  bridgeClose() {
    this.setVisibility(false);
    this.isAwaitingCallback = false;
    this.bridgeState = null;
    this.settingButtonFocus = false;
    this.checkValidation = false;
    this.focusDoctorListing();
  }

  /**
   * Resets the validation when transitioning to a new question.
   */
  resetValidationState() {
    this.checkValidation = false;
    this.selectedAnswers = null;
  }

  /**
   * Moves focus to the top of the doctor listing page.
   */
  focusDoctorListing() {
    // move focus back to alert, if it exists and is visible
    const alertParent = document.querySelector('#doctor-list-alert-parent') as HTMLElement;
    const isVisibleAlert = alertParent && (alertParent.getAttribute('show-alert') === '' || alertParent.getAttribute('show-alert') === 'true');
    if (isVisibleAlert) {
      /**
       * hack to fix A11y issue FC-9583
       * there is a modal error that shows up at the same time as the alert
       * and upon closing of the modal, it scrolls to the top of the screen.
       * We want to scroll to the alert, but it doesn't work unless we set a timeout
       */

      const alert = document.querySelector('#doctor-list-alert') as HTMLElement;
      alert?.focus({ preventScroll: true });
      setTimeout(() => {
        alert?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      }, 300);

      return;
    }

    // if there is no visible alert, move focus to query information header
    const queryInfo = document.querySelector(`.${QUERY_INFO_CLASS}`) as HTMLElement;
    if (queryInfo) {
      queryInfo.focus();
    }
  }

  getHeader = () => (this.bridgeState?.bridgeData?.title ? this.bridgeState.bridgeData?.title : this.header);

  renderControl(bridgeState: BridgeState, checkValidation: boolean) {
    if (!bridgeState?.bridgeData?.questionType) {
      return <div />;
    }

    //Just remove this when we get the real controls.
    const bridgeData = JSON.stringify(bridgeState.bridgeData);
    const previousAnswer = JSON.stringify(bridgeState.previousAnswer);

    //Replace your the returns with the objects
    switch (bridgeState.bridgeData.questionType) {
      case QuestionType.error:
        return (
          <fccd-modal-error-message
            message={bridgeState.bridgeData.message}
            error-text={(bridgeState.bridgeData as BridgeError).errorText}
            doctor-name={(bridgeState.bridgeData as BridgeError).doctorName}
            doctor-specialty={(bridgeState.bridgeData as BridgeError).doctorSpecialty}
            doctorImageUrl={(bridgeState.bridgeData as BridgeError).doctorImageUrl}
          />
        );
      case QuestionType.radio:
        return <fccd-modal-radio-question data={bridgeData} check-validation={checkValidation} previous-answer={previousAnswer} />;
      case QuestionType.age:
        return <fccd-age-and-status-question data={bridgeData} check-validation={checkValidation} previous-answer={previousAnswer} />;
      case QuestionType.select:
        return <fccd-modal-select-question data={bridgeData} check-validation={checkValidation} previous-answer={previousAnswer} />;
      case QuestionType.card:
        return <fccd-modal-card-question data={bridgeData} scenario={bridgeState.scenario} />;
      case QuestionType.telephone:
        return <fccd-modal-telephone-question data={bridgeData} check-validation={checkValidation} previous-answer={previousAnswer} />;
      case QuestionType.summary:
        return <fccd-modal-summary-question data={bridgeData} />;

      default:
        return <div></div>;
    }
  }

  render() {
    const { renderControl } = this;

    return (
      <hp5-modal-basic
        always-dismiss={true}
        cancel-button-disabled={this.isAwaitingCallback}
        cancel-button-text={this.bridgeState?.bridgeData?.backButton}
        confirm-button-disabled={this.isAwaitingCallback}
        confirm-button-hidden={!this.bridgeState?.bridgeData?.nextButton}
        confirm-button-text={this.bridgeState?.bridgeData?.nextButton}
        scrolling="fixed"
        stay-open-on-cancel={true}
        stay-open-on-confirm={true}
        title-text={this.getHeader()}
        title-size-selection="md"
        visible={this.isVisible}
        narrow={true}
      >
        {this.isAwaitingCallback ? <hp5-loading-panel loading-spinner-size="sm" state="spinning" message="Loading" /> : renderControl(this.bridgeState, this.checkValidation)}
      </hp5-modal-basic>
    );
  }
}
